import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { inquiryService, Inquiry } from 'src/services/inquiryService';
import { useToast } from 'src/components/ui/UseToast';

// Query keys for react-query
export const INQUIRIES_QUERY_KEYS = {
  all: ['inquiries'] as const,
  lists: () => [...INQUIRIES_QUERY_KEYS.all, 'list'] as const,
  allInquiries: (limit?: number) => [...INQUIRIES_QUERY_KEYS.lists(), 'all', limit] as const,
  newInquiries: (limit?: number) => [...INQUIRIES_QUERY_KEYS.lists(), 'new', limit] as const,
};

/**
 * Hook to fetch all inquiries with React Query
 * Uses 15 minutes stale time as requested
 */
export const useInquiries = (limit?: number) => {
  return useQuery({
    queryKey: INQUIRIES_QUERY_KEYS.allInquiries(limit),
    queryFn: () => inquiryService.fetchInquiries(limit),
    staleTime: 15 * 60 * 1000, // 15 minutes
    gcTime: 20 * 60 * 1000, // 20 minutes (formerly cacheTime)
    refetchOnWindowFocus: false,
    retry: 2,
  });
};

/**
 * Hook to fetch only new inquiries with React Query
 * Uses 15 minutes stale time as requested
 */
export const useNewInquiries = (limit?: number) => {
  return useQuery({
    queryKey: INQUIRIES_QUERY_KEYS.newInquiries(limit),
    queryFn: () => inquiryService.fetchNewInquiries(limit),
    staleTime: 15 * 60 * 1000, // 15 minutes
    gcTime: 20 * 60 * 1000, // 20 minutes
    refetchOnWindowFocus: false,
    retry: 2,
  });
};

/**
 * Hook to update inquiry status with optimistic updates
 */
export const useUpdateInquiryStatus = () => {
  const queryClient = useQueryClient();
  const { toast } = useToast();

  return useMutation({
    mutationFn: ({ id, status }: { id: string; status: string }) =>
      inquiryService.updateInquiryStatus(id, status),
    onMutate: async ({ id, status }) => {
      // Cancel any outgoing refetches
      await queryClient.cancelQueries({ queryKey: INQUIRIES_QUERY_KEYS.all });

      // Snapshot the previous value
      const previousAllInquiries = queryClient.getQueriesData({
        queryKey: INQUIRIES_QUERY_KEYS.lists(),
      });

      // Optimistically update all inquiry queries
      queryClient.setQueriesData(
        { queryKey: INQUIRIES_QUERY_KEYS.lists() },
        (old: Inquiry[] | undefined) => {
          if (!old) return old;
          return old.map((inquiry) =>
            inquiry.id === id ? { ...inquiry, status } : inquiry
          );
        }
      );

      return { previousAllInquiries };
    },
    onError: (err, variables, context) => {
      // Revert the optimistic update
      if (context?.previousAllInquiries) {
        context.previousAllInquiries.forEach(([queryKey, data]) => {
          queryClient.setQueryData(queryKey, data);
        });
      }
      toast({
        title: "Error",
        description: "Failed to update inquiry status",
        variant: "destructive",
      });
    },
    onSuccess: () => {
      toast({
        title: "Status Updated",
        description: "Inquiry status has been updated",
      });
    },
    onSettled: () => {
      // Always refetch after error or success
      queryClient.invalidateQueries({ queryKey: INQUIRIES_QUERY_KEYS.all });
    },
  });
};

/**
 * Hook to delete inquiry with optimistic updates
 */
export const useDeleteInquiry = () => {
  const queryClient = useQueryClient();
  const { toast } = useToast();

  return useMutation({
    mutationFn: (id: string) => inquiryService.deleteInquiry(id),
    onMutate: async (id) => {
      // Cancel any outgoing refetches
      await queryClient.cancelQueries({ queryKey: INQUIRIES_QUERY_KEYS.all });

      // Snapshot the previous value
      const previousAllInquiries = queryClient.getQueriesData({
        queryKey: INQUIRIES_QUERY_KEYS.lists(),
      });

      // Optimistically remove the inquiry from all queries
      queryClient.setQueriesData(
        { queryKey: INQUIRIES_QUERY_KEYS.lists() },
        (old: Inquiry[] | undefined) => {
          if (!old) return old;
          return old.filter((inquiry) => inquiry.id !== id);
        }
      );

      return { previousAllInquiries };
    },
    onError: (err, variables, context) => {
      // Revert the optimistic update
      if (context?.previousAllInquiries) {
        context.previousAllInquiries.forEach(([queryKey, data]) => {
          queryClient.setQueryData(queryKey, data);
        });
      }
      toast({
        title: "Error",
        description: "Failed to delete inquiry",
        variant: "destructive",
      });
    },
    onSuccess: () => {
      toast({
        title: "Inquiry Deleted",
        description: "The inquiry has been permanently removed",
      });
    },
    onSettled: () => {
      // Always refetch after error or success
      queryClient.invalidateQueries({ queryKey: INQUIRIES_QUERY_KEYS.all });
    },
  });
};

/**
 * Combined hook that provides all inquiry data
 * This ensures data is fetched once and shared across components
 */
export const useInquiriesData = (allLimit?: number, newLimit?: number) => {
  const allInquiriesQuery = useInquiries(allLimit);
  const newInquiriesQuery = useNewInquiries(newLimit);
  
  return {
    // All inquiries
    allInquiries: allInquiriesQuery.data || [],
    isLoadingAll: allInquiriesQuery.isLoading,
    allError: allInquiriesQuery.error?.message || null,
    refetchAll: allInquiriesQuery.refetch,
    
    // New inquiries
    newInquiries: newInquiriesQuery.data || [],
    isLoadingNew: newInquiriesQuery.isLoading,
    newError: newInquiriesQuery.error?.message || null,
    refetchNew: newInquiriesQuery.refetch,
    
    // Combined states
    isLoading: allInquiriesQuery.isLoading || newInquiriesQuery.isLoading,
    hasError: !!(allInquiriesQuery.error || newInquiriesQuery.error),
    error: allInquiriesQuery.error?.message || newInquiriesQuery.error?.message || null,
    
    // Computed values
    newCount: newInquiriesQuery.data?.length || 0,
    
    // Refresh all
    refreshAll: () => {
      allInquiriesQuery.refetch();
      newInquiriesQuery.refetch();
    }
  };
};